import { useQuery } from "@tanstack/react-query";
import { useCallback, useContext, useMemo, useState } from "react";

import { type BuilderInfo } from "helpers/AppProvider.types";
import { AppDefaultInfoContext } from "helpers/globalContext/AppDefaultInfoContext";
import { queryClient } from "helpers/queryClient";

import { getSearchApiVersion } from "commonComponents/utilities/MainNavigation/AiSearch/aiSearchFlags";
import { useAuthToken } from "commonComponents/utilities/MainNavigation/AiSearch/hooks/useAuthToken";
import { useDebouncedValue } from "commonComponents/utilities/MainNavigation/SearchDialog/results/useDebouncedValue";

import {
    addRecentQuestion,
    fetchAskSuggestCounts,
    fetchAskSuggestions,
    getRecentQuestions,
    type IAskQuery,
    type IAskSuggestions,
    type IAskSuggestSubject,
    matchRecentQuestions,
    NO_SUGGESTIONS,
} from "./askSuggest";

/**
 * How long typing pauses before the box asks. Short: the service reads names
 * held in memory, and a suggestion a beat late is one the user has typed past.
 */
export const SUGGEST_PAUSE_MS = 90;

/** How long suggestions for a text, and the counts for a list, are reused. */
const SUGGEST_CACHE_MS = 5 * 60_000;
const COUNT_CACHE_MS = 2 * 60_000;

const NO_COUNTS: ReadonlyMap<string, number | null> = new Map();

export interface IUseAskSuggestOptions {
    text: string;
    /** The conversation's subject: an empty box suggests follow-ups about it. */
    subject: IAskSuggestSubject | null;
    /** Off while an answer streams or the agent is unavailable. */
    enabled: boolean;
    /** The user the recent questions belong to ("builder:user"). */
    owner: string;
}

export interface IUseAskSuggestResult {
    /** The suggestions for `forText`: the last text answered, which can trail the box. */
    suggestions: IAskSuggestions;
    forText: string;
    /** Recent questions the typed words could be heading back to. */
    recent: string[];
    /**
     * Counts by query, read once the list is drawn: a number, null where it
     * could not be read, or absent while it is being read.
     */
    counts: ReadonlyMap<string, number | null>;
    /** Remembers a question asked, to offer it again. */
    remember: (question: string) => void;
}

/** The key a count is kept under: the same question on another keystroke reads it again. */
export function countKey(query: IAskQuery): string {
    return JSON.stringify([query.entity_type, query.filters]);
}

/**
 * Suggestions while a question is typed, then the counts behind the questions
 * on offer. Debounced and cached per text; a text answered before shows at once.
 */
export function useAskSuggest(
    builderInfo: BuilderInfo | null | undefined,
    { text, subject, enabled, owner }: IUseAskSuggestOptions,
): IUseAskSuggestResult {
    const appDefaultInfo = useContext(AppDefaultInfoContext);
    const searchConfig = appDefaultInfo?.searchServiceConfiguration;
    const searchBaseUrl = searchConfig?.apiBaseUrl ?? "";
    const searchApiVersion = getSearchApiVersion(builderInfo);
    const { getToken, clearToken } = useAuthToken(searchConfig?.authAudience ?? "");
    const live = enabled && searchApiVersion === "v2" && searchBaseUrl.length > 0;

    const subjectKey = subject === null ? "" : `${subject.entity}:${subject.id}:${subject.title}`;
    const suggestKey = (forText: string) =>
        ["askSuggest", searchBaseUrl, searchApiVersion, subjectKey, forText] as const;
    const debounced = useDebouncedValue(text, SUGGEST_PAUSE_MS);
    // A text answered before is read at once, without waiting out the pause.
    const asked = queryClient.getQueryData(suggestKey(text)) !== undefined ? text : debounced;

    const { data: answered } = useQuery(
        suggestKey(asked),
        async ({ signal }) => {
            const suggestions = await fetchAskSuggestions({
                searchBaseUrl,
                searchApiVersion,
                getToken,
                clearToken,
                signal,
                text: asked,
                subject,
            });
            if (suggestions === null) {
                // Not cached: the next keystroke asks again.
                throw new Error("suggestions unavailable");
            }
            return { text: asked, suggestions };
        },
        {
            enabled: live,
            keepPreviousData: true,
            staleTime: SUGGEST_CACHE_MS,
            cacheTime: SUGGEST_CACHE_MS,
            retry: false,
            refetchOnWindowFocus: false,
        },
    );
    const suggestions = live && answered !== undefined ? answered.suggestions : NO_SUGGESTIONS;

    // The counts behind the questions on offer, read after the list is drawn.
    const queries = useMemo(
        () => suggestions.questions.flatMap((q) => (q.query === null ? [] : [q.query])),
        [suggestions],
    );
    const { data: counts = NO_COUNTS } = useQuery(
        ["askSuggestCounts", searchBaseUrl, searchApiVersion, queries.map(countKey)],
        async ({ signal }) => {
            const read = await fetchAskSuggestCounts({
                searchBaseUrl,
                searchApiVersion,
                getToken,
                clearToken,
                signal,
                queries,
            });
            if (read === null) {
                // Not cached: the questions stand without counts, and are asked again.
                throw new Error("counts unavailable");
            }
            // A count the service could not read in time is null: kept, without a number.
            return new Map(queries.map((q, i) => [countKey(q), read[i] ?? null]));
        },
        {
            enabled: live && queries.length > 0,
            keepPreviousData: true,
            staleTime: COUNT_CACHE_MS,
            cacheTime: COUNT_CACHE_MS,
            retry: false,
            refetchOnWindowFocus: false,
        },
    );

    const [recentQuestions, setRecentQuestions] = useState(() => getRecentQuestions(owner));
    const recent = useMemo(
        () => (text.trim().length >= 2 ? matchRecentQuestions(recentQuestions, text) : []),
        [text, recentQuestions],
    );
    const remember = useCallback(
        (question: string) => {
            addRecentQuestion(owner, question);
            setRecentQuestions(getRecentQuestions(owner));
        },
        [owner],
    );

    return {
        suggestions,
        forText: answered?.text ?? "",
        recent,
        counts,
        remember,
    };
}
